export function isValidPythonRegex(pattern: string) {
  try {
    // Check for JS-specific features that Python doesn't support
    const jsOnlyFeatures = [
      /(?<!\\)\/[gimuy]*$/, // JS regex literals with flags
      /\\k<\w+>/, // JS named backreferences
      /\(\?>[^)]*\)/, // atomic groups (not in Python `re` module)
      /\\p{[^}]+}/, // unicode property escapes (limited Python support)
      /\\P{[^}]+}/, // negated unicode property escapes
    ];

    // Check for invalid JS features in Python context
    for (const jsFeature of jsOnlyFeatures) {
      if (jsFeature.test(pattern)) {
        return false;
      }
    }

    // Check for Python-specific valid features
    const pythonFeatures = {
      namedGroups: /\(\?P<[A-Z_a-z]\w*>/g,
      namedBackrefs: /\(\?P=[A-Z_a-z]\w*\)/g,
      comments: /\(\?#[^)]*\)/g,
      conditionals: /\(\?\([^)]+\)[^|]*\|?[^)]*\)/g,
      inlineFlags: /\(\?[Laimsux]+\)/g,
      groupFlags: /\(\?[Laimsux]*-[Laimsux]*:/g,
    };

    // Validate Python named groups have valid names
    const namedGroups = pattern.match(pythonFeatures.namedGroups);
    if (namedGroups) {
      for (const group of namedGroups) {
        const name = group.match(/\(\?P<([^>]+)>/)?.[1];
        if (name && !/^[A-Z_a-z]\w*$/.test(name)) {
          return false;
        }
      }
    }

    // Check for invalid escape sequences
    const invalidEscapes = [
      /\\[0-7]{4,}/, // octal escapes too long
      /\\x(?![\dA-Fa-f]{2})/, // invalid hex escapes (not exactly 2 hex digits)
      /\\x[\dA-Fa-f](?![\dA-Fa-f])/, // single hex digit after \x
      /\\u(?![\dA-Fa-f]{4})/, // invalid unicode escapes (not exactly 4 hex digits)
      /\\u[\dA-Fa-f]{1,3}(?![\dA-Fa-f])/, // too short unicode escapes
      /\\u[\dA-Fa-f]{5,}/, // too long unicode escapes
      /\\U(?![\dA-Fa-f]{8})/, // invalid long unicode escapes (not exactly 8 hex digits)
      /\\U[\dA-Fa-f]{1,7}(?![\dA-Fa-f])/, // too short long unicode escapes
      /\\U[\dA-Fa-f]{9,}/, // too long long unicode escapes
    ];

    for (const invalidEscape of invalidEscapes) {
      if (invalidEscape.test(pattern)) {
        return false;
      }
    }

    // Check for unescaped special characters in wrong contexts
    if (!validateBrackets(pattern)) {
      return false;
    }

    if (!validateQuantifiers(pattern)) {
      return false;
    }

    if (!validateGroups(pattern)) {
      return false;
    }

    // Try to catch remaining syntax errors by creating a simplified version
    // and testing with JS RegExp (removing Python-specific features)
    const simplifiedPattern = pattern
      .replaceAll(/\(\?P<[^>]+>/g, "(") // replace named groups
      .replaceAll(/\(\?P=[^)]+\)/g, "\\1") // replace named backrefs with numbered
      .replaceAll(/\(\?#[^)]*\)/g, "") // remove comments
      .replaceAll("\\A", "^") // replace Python anchors
      .replaceAll("\\Z", "$")
      .replaceAll(/\(\?[Laimsux]+\)/g, "") // remove inline flags
      .replaceAll(/\(\?[Laimsux]*-[Laimsux]*:/g, "(?:"); // replace group flags

    // Test the simplified pattern
    new RegExp(simplifiedPattern);

    return true;
  } catch {
    return false;
  }
}

function validateBrackets(pattern: string) {
  let bracketDepth = 0;
  let inCharClass = false;

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];
    const prevChar = i > 0 ? pattern[i - 1] : "";

    if (prevChar === "\\") {
      continue; // Skip escaped characters
    }

    if (char === "[" && !inCharClass) {
      inCharClass = true;
    } else if (char === "]" && inCharClass) {
      inCharClass = false;
    } else if (char === "(" && !inCharClass) {
      bracketDepth++;
    } else if (char === ")" && !inCharClass) {
      bracketDepth--;
      if (bracketDepth < 0) {
        return false; // unmatched closing parenthesis
      }
    }
  }

  return bracketDepth === 0 && !inCharClass;
}

function validateQuantifiers(pattern: string) {
  const quantifiers = /[*+?{]/g;
  let match;

  while ((match = quantifiers.exec(pattern)) !== null) {
    const pos = match.index;

    // Check if quantifier is escaped
    let backslashes = 0;
    for (let i = pos - 1; i >= 0 && pattern[i] === "\\"; i--) {
      backslashes++;
    }
    if (backslashes % 2 === 1) {
      continue; // escaped quantifier
    }

    // Handle curly brace quantifiers
    if (pattern[pos] === "{") {
      const closingBrace = pattern.indexOf("}", pos);
      if (closingBrace === -1) {
        return false; // unclosed brace
      }

      const content = pattern.slice(pos + 1, closingBrace);

      // Validate the content inside braces - should be "n", "n,m" or "n,"
      // Valid formats: {5}, {3,}, {2,7}
      if (!/^\d+(?:,\d*)?$/.test(content)) {
        return false; // invalid quantifier syntax (like {1-3})
      }

      // Check for optional non-greedy modifier after closing brace
      let nextPos = closingBrace + 1;
      if (nextPos < pattern.length && pattern[nextPos] === "?") {
        nextPos++; // skip the non-greedy modifier
      }

      // Skip past the quantifier for the next iteration
      quantifiers.lastIndex = nextPos;
      continue;
    }

    // Check if quantifier is at the beginning
    if (pos === 0) {
      return false;
    }

    // Check if this is a non-greedy quantifier (quantifier followed by ?)
    const isNonGreedy = pos + 1 < pattern.length && pattern[pos + 1] === "?";

    // Check if quantifier comes after another quantifier
    const prevChar = pattern[pos - 1];
    if (prevChar && /[*+?]/.test(prevChar)) {
      // Check if the previous quantifier is properly escaped
      let prevBackslashes = 0;
      for (let i = pos - 2; i >= 0 && pattern[i] === "\\"; i--) {
        prevBackslashes++;
      }
      // Special case: ?? is valid (non-greedy ?)
      if (
        prevBackslashes % 2 === 0 &&
        !(prevChar === "?" && pattern[pos] === "?")
      ) {
        return false; // two unescaped quantifiers in a row
      }
    }

    // If this is a non-greedy quantifier, skip the ? in the next iteration
    if (isNonGreedy) {
      quantifiers.lastIndex = pos + 2;
    }
  }

  return true;
}

function validateGroups(pattern: string) {
  const groupPattern = /\([^)]*\)/g;
  let match;

  while ((match = groupPattern.exec(pattern)) !== null) {
    const group = match[0];

    // Check for valid group syntax
    if (group.startsWith("(?")) {
      const content = group.slice(2, -1);

      // Valid Python group types
      const validGroupTypes = [
        /^P<[A-Z_a-z]\w*>.*$/, // named groups
        /^P=[A-Z_a-z]\w*$/, // named backreferences
        /^#.*$/, // comments
        /^[Laimsux]+$/, // inline flags
        /^[Laimsux]*-[Laimsux]*:.*$/, // group flags
        /^:.*$/, // non-capturing groups
        /^=.*$/, // positive lookahead
        /^!.*$/, // negative lookahead
        /^<=.*$/, // positive lookbehind
        /^<!.*$/, // negative lookbehind
        /^\([^)]+\).*$/, // conditional groups (basic check)
      ];

      const isValid = validGroupTypes.some((type) => type.test(content));
      if (!isValid && content !== "") {
        return false;
      }
    }
  }

  return true;
}
